import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { useI18n } from "../context/I18nContext";

export default function Register() {
  const { register } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [isCanadian, setIsCanadian] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError(t("auth_pwd_mismatch")); return; }
    if (password.length < 6) { setError(t("auth_pwd_short")); return; }
    if (!isCanadian) { setError(t("auth_must_attest")); return; }
    setLoading(true);
    try {
      await register(email, password, isCanadian);
      navigate("/paywall", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F4F4F4] text-black flex items-center justify-center px-5 py-10" data-testid="register-page">
      <div className="w-full max-w-md bg-white border-2 border-black brutal-shadow p-8">
        <div className="font-mono-civic text-xs uppercase tracking-[0.3em] text-[#E00000]">{t("auth_register_tag")}</div>
        <h1 className="mt-2 font-serif-display font-extrabold text-4xl text-black leading-tight">{t("auth_register_h1")}</h1>
        <p className="mt-2 font-bold text-black">{t("auth_register_sub")}</p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="register-form">
          <div>
            <label className="block font-mono-civic text-xs uppercase tracking-widest text-black mb-2">{t("auth_email")}</label>
            <input
              type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border-2 border-black px-4 py-3 font-bold text-black bg-white focus:outline-none focus:border-[#E00000]"
              data-testid="register-email-input"
            />
          </div>
          <div>
            <label className="block font-mono-civic text-xs uppercase tracking-widest text-black mb-2">{t("auth_password")}</label>
            <input
              type="password" required minLength={6} value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border-2 border-black px-4 py-3 font-bold text-black bg-white focus:outline-none focus:border-[#E00000]"
              data-testid="register-password-input"
            />
          </div>
          <div>
            <label className="block font-mono-civic text-xs uppercase tracking-widest text-black mb-2">{t("auth_confirm_password")}</label>
            <input
              type="password" required minLength={6} value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full border-2 border-black px-4 py-3 font-bold text-black bg-white focus:outline-none focus:border-[#E00000]"
              data-testid="register-confirm-input"
            />
          </div>

          <label
            htmlFor="is-canadian-checkbox"
            className="flex items-start gap-3 border-2 border-black bg-[#F4F4F4] p-4 cursor-pointer hover:bg-white transition-colors"
            data-testid="register-canadian-label"
          >
            <input
              id="is-canadian-checkbox" type="checkbox" checked={isCanadian}
              onChange={(e) => setIsCanadian(e.target.checked)}
              className="mt-1 w-5 h-5 accent-[#E00000] shrink-0"
              data-testid="register-canadian-checkbox"
            />
            <span className="font-bold text-black text-sm leading-snug">
              {t("auth_canadian_attest")}
            </span>
          </label>

          {error && (
            <div className="border-2 border-[#E00000] bg-white p-3 font-bold text-[#E00000] text-sm" data-testid="register-error">
              {error}
            </div>
          )}
          <button
            type="submit" disabled={loading}
            className="w-full bg-black text-white font-bold uppercase tracking-widest py-4 border-2 border-black brutal-shadow-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all disabled:opacity-60"
            data-testid="register-submit-btn"
          >
            {loading ? t("auth_register_loading") : t("auth_register_submit")}
          </button>
        </form>

        <p className="mt-6 font-bold text-black text-sm">
          {t("auth_have_account")}{" "}
          <Link to="/login" className="underline" data-testid="register-to-login">{t("auth_login_link")}</Link>
        </p>
      </div>
    </div>
  );
}
